"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, useRouter, usePathname } from "next/navigation";
import { postLead } from "@services/leadCaptureService";
import { postRequestCallback } from "@services/requestCallbackService";
import { addUTMParamsToLeadData } from "@utils/utmParams";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";
import styles from "./AckoDriveStepsClientWrapper.module.scss";
import { getLoginUrl } from "@utils/getLoginUrl";
import RequestCallBackModal from "@components/molecules/RequestCallBackModal";
import GlobalLoader from "@components/atoms/GlobalLoader/GlobalLoader";
import { getPlatform } from "@utils/platformDetection.client";

interface AckoDriveStepsClientWrapperProps {
  children: React.ReactNode;
  cityName?: string;
  brandName?: string;
  modelName?: string;
  carAvailability?: string;
  pageType?: string;
  variantName?: string;
  isLoggedIn: boolean;
  segmentEventProps: any;
}

export default function AckoDriveStepsClientWrapper({
  children,
  cityName,
  brandName,
  modelName,
  carAvailability,
  pageType = "MM page",
  variantName,
  isLoggedIn,
  segmentEventProps,
}: AckoDriveStepsClientWrapperProps) {
  const [isCallBackModalOpen, setIsCallBackModalOpen] = useState(false);
  const [isLoadingLead, setIsLoadingLead] = useState(false);
  const hasInitialized = useRef(false);
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  const processLeadAndShowModal = async () => {
    // Prevent duplicate calls
    if (isLoadingLead || hasInitialized.current) {
      return;
    }
    
    setIsLoadingLead(true);
    hasInitialized.current = true;
      let leadData: any = {
        city: cityName,
        make: brandName || "",
        model: modelName || "",
        variant: variantName || "",
        color: "",
      };

      // const storedSource = sessionStorage.getItem("referrerSource");
      const utmTerm =
        new URL(window.location.href).searchParams.get("utm_term") || "";
      leadData.dump_data = {
        mx_Platform: getPlatform(),
        mx_Page_Type_Web: pageType,
        mx_Login_Type: "Request callback",
        // SourceReferrerURL: storedSource,
        mx_URL: window.location.href || process.env.NEXT_PUBLIC_FRONTEND_HOST,
        mx_UTM_Term: utmTerm,
        mx_color: leadData?.color || "",
      };
      let additionalLeaddata = JSON.parse(
        sessionStorage.getItem("leadData") || "{}"
      );

      if (additionalLeaddata) {
        additionalLeaddata = { ...additionalLeaddata, ...leadData };
        leadData = { ...additionalLeaddata, ...leadData };
      } else {
        additionalLeaddata = { ...leadData };
      }
      const buyingPeriod = leadData?.buying_period || "";
      try {
        // Call both APIs in parallel using Promise.allSettled for better error handling
        const results = await Promise.allSettled([
          postLead(addUTMParamsToLeadData(leadData)),
          postRequestCallback({
            callback_message: `Request callback for ${brandName} ${modelName} in ${cityName}`,
          })
        ]);

        const [leadResult, callbackResult] = results;
        if (leadResult.status === 'fulfilled' && callbackResult.status === 'fulfilled') {
          setIsLoadingLead(false);
          setIsCallBackModalOpen(true);
        }

        // Handle lead response
        if (leadResult.status === 'fulfilled') {
          const leadResponse = leadResult.value;
          if (!leadResponse.success) {
            console.error("Lead capture failed.", leadResponse);
            await trackEventWithCommonProps(
              "exception",
              EventType.TRACK_EVENT,
              {
                pageType: pageType,
                additionalProps: {
                  exception_type: "API issue",
                  exception_name: "failed lead creation",
                  car_availability: carAvailability,
                  vehicle_make: brandName,
                  vehicle_model: modelName,
                  vehicle_variant: variantName,
                  city_name: cityName,
                  login_type: "Request callback",
                  intent: buyingPeriod,
                },
              }
            );
          } else {
            const leadCreated = !!leadResponse?.data?.is_created;
            await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
              pageType: pageType,
              additionalProps: {
                action_type: leadCreated ? "lead_created" : "lead_updated",
                car_availability: carAvailability,
                vehicle_make: brandName,
                vehicle_model: modelName,
                vehicle_variant: variantName,
                city_name: cityName,
                login_type: "Request callback",
                intent: buyingPeriod,
              },
            });
          }
        } else {
          console.error("Lead API call failed:", leadResult.reason);
          await trackEventWithCommonProps("exception", EventType.TRACK_EVENT, {
            pageType: pageType,
            additionalProps: {
              exception_type: "API issue",
              exception_name: "lead api call failed",
              car_availability: carAvailability,
              vehicle_make: brandName,
              vehicle_model: modelName,
              vehicle_variant: variantName,
              city_name: cityName,
              login_type: "Request callback",
              intent: buyingPeriod,
            },
          });
        }

        // Handle callback response
        if (callbackResult.status === 'fulfilled') {
          const callbackResponse = callbackResult.value;
          if (!callbackResponse.success) {
            console.error("Request callback failed:", callbackResponse.error);
            await trackEventWithCommonProps(
              "exception",
              EventType.TRACK_EVENT,
              {
                pageType: pageType,
                additionalProps: {
                  exception_type: "API issue",
                  exception_name: "failed callback request",
                  car_availability: carAvailability,
                  vehicle_make: brandName,
                  vehicle_model: modelName,
                  vehicle_variant: variantName,
                  city_name: cityName,
                  login_type: "Request callback",
                  intent: buyingPeriod,
                },
              }
            );
          }
        } else {
          console.error("Callback API call failed:", callbackResult.reason);
          await trackEventWithCommonProps("exception", EventType.TRACK_EVENT, {
            pageType: pageType,
            additionalProps: {
              exception_type: "API issue",
              exception_name: "callback api call failed",
              car_availability: carAvailability,
              vehicle_make: brandName,
              vehicle_model: modelName,
              vehicle_variant: variantName,
              city_name: cityName,
              login_type: "Request callback",
              intent: buyingPeriod,
            },
          });
        }
      } catch (error) {
        console.error("Unexpected error in API processing:", error);
        await trackEventWithCommonProps("exception", EventType.TRACK_EVENT, {
          pageType: pageType,
          additionalProps: {
            exception_type: "API issue",
            exception_name: "unexpected api error",
            car_availability: carAvailability,
            vehicle_make: brandName,
            vehicle_model: modelName,
            vehicle_variant: variantName,
            city_name: cityName,
            login_type: "Request callback",
            intent: buyingPeriod,
          },
        });
      }
    
  }

  const handleModalClose = () => {
    setIsCallBackModalOpen(false);
    const callbackReqParam = searchParams?.get("callbackReq");
    if(!callbackReqParam) return;
    
    // Clean up URL only
    const currentParams = searchParams ? searchParams.toString() : '';
    const newSearchParams = new URLSearchParams(currentParams);
    newSearchParams.delete('callbackReq');
    const newUrl = newSearchParams.toString() ? `${pathname}?${newSearchParams.toString()}` : pathname;
    router.replace(newUrl || "", { scroll: false });
  };

  useEffect(() => {
    const callbackReqParam = searchParams?.get("callbackReq");
    if (callbackReqParam === 'true' && isLoggedIn && !hasInitialized.current) {
      processLeadAndShowModal();
    }
  }, []); // Empty dependency array with ref protection

  const clickHandler = () => {
    
    if (!isLoggedIn) {
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set('callbackReq', 'true');
      const redirectUrl = currentUrl.toString();
      if(typeof window === "undefined"){
        sessionStorage.setItem("loginType", "Request callback");
        localStorage.setItem("MMV", JSON.stringify({
          make: brandName,
          model: modelName,
          variant: variantName,
          color: "",
        }));
      }
      window.location.href = getLoginUrl(redirectUrl);
      return;
    }
    processLeadAndShowModal();
  };

  return (
    <>
      <div className={styles.wrapper} onClick={clickHandler}>
        {children}
      </div>
      <RequestCallBackModal
        isOpen={isCallBackModalOpen}
        onClose={handleModalClose}
      />
      <GlobalLoader
        isLoading={isLoadingLead}
        variant="spinner"
        size="medium"
        overlay={true}
        text="Processing your request..."
      />
    </>
  );
} 